import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../../ui';
import {
  HomeIcon,
  ClipboardDocumentCheckIcon,
  CalendarDaysIcon,
  BookOpenIcon,
  HeartIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/solid';

interface TourStepProps {
  onComplete: () => void;
}

const tourSlides = [
  {
    icon: HomeIcon,
    title: 'Dashboard',
    description: 'Your home base. See your sobriety counters, daily reflection, and quick actions all in one place.',
    color: 'bg-accent-100 dark:bg-accent-900/30 text-accent-600 dark:text-accent-400',
  },
  {
    icon: ClipboardDocumentCheckIcon,
    title: 'Daily Check-in',
    description: 'Start each day by confirming your sobriety and setting your intentions. Your sponsor can see these too.',
    color: 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400',
  },
  {
    icon: CalendarDaysIcon,
    title: 'Nightly Review',
    description: 'Reflect on your day with questions from the Big Book. Get AI-powered insights to support your growth.',
    color: 'bg-accent-100 dark:bg-accent-900/30 text-accent-600 dark:text-accent-400',
  },
  {
    icon: BookOpenIcon,
    title: 'Big Book Reader',
    description: 'Access the full text of the Big Book (2nd Edition) anytime, even offline. Highlight and take notes.',
    color: 'bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400',
  },
  {
    icon: HeartIcon,
    title: 'Gratitude Journal',
    description: 'Cultivate gratitude daily. Research shows this practice strengthens recovery and mental health.',
    color: 'bg-accent-100 dark:bg-accent-900/30 text-accent-600 dark:text-accent-400',
  },
  {
    icon: ExclamationTriangleIcon,
    title: 'SOS Button',
    description: "When you're struggling, the SOS button connects you instantly to your sponsor and crisis resources.",
    color: 'bg-danger-100 dark:bg-danger-900/30 text-danger-600 dark:text-danger-400',
  },
];

export function TourStep({ onComplete }: TourStepProps) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const slide = tourSlides[currentSlide];
  const isLastSlide = currentSlide === tourSlides.length - 1;

  const nextSlide = () => {
    if (isLastSlide) {
      onComplete();
    } else {
      setCurrentSlide((prev) => prev + 1);
    }
  };

  const prevSlide = () => setCurrentSlide((prev) => Math.max(0, prev - 1));

  return (
    <div>
      <motion.div className="text-center mb-6" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <p className="text-surface-600 dark:text-surface-400">Let's take a quick look at what you can do</p>
      </motion.div>

      {/* Slide */}
      <div className="relative bg-white dark:bg-surface-800 rounded-2xl p-8 border border-surface-200 dark:border-surface-700 min-h-[280px] overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="text-center"
          >
            <div className={`inline-flex p-4 rounded-2xl ${slide.color} mb-6`}>
              <slide.icon className="w-10 h-10" />
            </div>
            <h3 className="text-xl font-display font-semibold text-surface-800 dark:text-white mb-3">{slide.title}</h3>
            <p className="text-surface-600 dark:text-surface-400 max-w-sm mx-auto">{slide.description}</p>
          </motion.div>
        </AnimatePresence>

        {/* Navigation arrows */}
        {currentSlide > 0 && (
          <button onClick={prevSlide} className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-surface-100 dark:bg-surface-700 hover:bg-surface-200 dark:hover:bg-surface-600 transition-colors">
            <ChevronLeftIcon className="w-5 h-5 text-surface-600 dark:text-surface-300" />
          </button>
        )}
        <button onClick={nextSlide} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-surface-100 dark:bg-surface-700 hover:bg-surface-200 dark:hover:bg-surface-600 transition-colors">
          <ChevronRightIcon className="w-5 h-5 text-surface-600 dark:text-surface-300" />
        </button>
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-2 mt-6">
        {tourSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentSlide ? 'w-6 bg-accent-500' : 'bg-surface-300 dark:bg-surface-600'
            }`}
          />
        ))}
      </div>

      <motion.div className="pt-6 space-y-3" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
        <Button onClick={nextSlide} size="lg" className="w-full">
          {isLastSlide ? "I'm Ready!" : 'Next'}
        </Button>
        {!isLastSlide && (
          <button onClick={onComplete} className="w-full text-sm text-surface-500 hover:text-surface-700 dark:text-surface-400 dark:hover:text-surface-200 transition-colors">
            Skip tour
          </button>
        )}
      </motion.div>
    </div>
  );
}
